import React, { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react';
import axios from '../api/client';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8004';
const POLL_MS = 1800;

export const UploadContext = createContext(null);

export function UploadProvider({ children }) {
  const [jobs, setJobs] = useState([]);
  const pollRefs = useRef({});

  /**
   * completionNotice — set once when ALL active jobs finish in a batch.
   * Shape: { totalProcessed: number, timestamp: number } | null
   * Consumers call clearCompletionNotice() after acting on it.
   */
  const [completionNotice, setCompletionNotice] = useState(null);
  const prevActiveCountRef = useRef(0);

  // Fire a persistent toast the first time each job settles into duplicate/error
  useEffect(() => {
    jobs.forEach(j => {
      if (j.dismissed || seenJobToastIds.current.has(j.id)) return;
      if (j.status === 'duplicate') {
        seenJobToastIds.current.add(j.id);
        const poLabel = j.duplicate_info?.duplicates?.[0]?.po_number
          ? `PO ${j.duplicate_info.duplicates[0].po_number}`
          : 'A PO';
        setToasts(prev => [...prev, {
          id: j.id + '_dup',
          severity: 'warning',
          msg: `${poLabel} already exists in the database. Open the upload widget (bottom-right) and choose "View Diff & Override" to update it, or "View in Audit" to review the existing record.`,
        }]);
      } else if (j.status === 'error') {
        seenJobToastIds.current.add(j.id);
        const vendorLabel = VENDOR_LABELS[j.vendor] || j.vendor || 'the selected vendor';
        const msg = (j.message && !j.message.startsWith('Processing '))
          ? j.message
          : `Could not extract data from the uploaded PDF(s). Make sure you are uploading a valid ${vendorLabel} PO.`;
        setToasts(prev => [...prev, {
          id: j.id + '_err',
          severity: 'error',
          msg,
        }]);
      }
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobs]);

  // Watch jobs — fire completionNotice when active count drops to 0 from > 0.
  // Also counts mixed duplicate-jobs that successfully saved ≥1 new PO so those
  // new POs are surfaced via the banner and auto-navigation just like 'done' jobs.
  useEffect(() => {
    const active = jobs.filter(j => !j.dismissed && ['queued', 'processing'].includes(j.status));

    // "Settled with results" = done jobs  OR  duplicate jobs that also saved new POs
    const settledWithResults = jobs.filter(j => !j.dismissed && (
      j.status === 'done' ||
      (j.status === 'duplicate' && (j.processed_files || 0) > 0 &&
       Array.isArray(j.result_po_ids) && j.result_po_ids.length > 0)
    ));

    if (prevActiveCountRef.current > 0 && active.length === 0 && settledWithResults.length > 0) {
      const total = settledWithResults.reduce((sum, j) => sum + (j.processed_files || j.total || 0), 0);
      if (total > 0) {
        setCompletionNotice({ totalProcessed: total, timestamp: Date.now() });
      }
    }
    prevActiveCountRef.current = active.length;
  }, [jobs]);

  const clearCompletionNotice = useCallback(() => setCompletionNotice(null), []);

  // Global toast queue — survives page navigation because it lives in context.
  // Shape: { id, severity, msg }[]
  const [toasts, setToasts] = useState([]);
  const seenJobToastIds = useRef(new Set());

  const dismissToast = useCallback((id) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  const VENDOR_LABELS = {
    american_eagle: 'American Eagle', chico: "Chico's",
    freepeople: 'Free People', lane_bryant: 'Lane Bryant',
    mast: 'MAST', loft: 'LOFT', talbots: 'Talbots',
  };

  const _patchJob = useCallback((jobId, patch) => {
    setJobs(prev => prev.map(j => (j.id === jobId ? { ...j, ...patch } : j)));
  }, []);

  const _stopPoll = useCallback((jobId) => {
    if (pollRefs.current[jobId]) {
      clearInterval(pollRefs.current[jobId]);
      delete pollRefs.current[jobId];
    }
  }, []);

  const _startPoll = useCallback((jobId) => {
    const tick = async () => {
      try {
        const { data } = await axios.get(`${API_BASE_URL}/api/jobs/${jobId}`);
        _patchJob(jobId, data);
        if (['done', 'error', 'duplicate'].includes(data.status)) {
          _stopPoll(jobId);
        }
      } catch (e) {
        if (e?.response?.status === 404) {
          // Job no longer exists on server (e.g. Railway redeploy cleared in-memory jobs).
          // Stop polling and surface a clear error so the widget doesn't stay stuck.
          _stopPoll(jobId);
          _patchJob(jobId, {
            status: 'error',
            message: 'Job no longer available — the server was restarted. Please re-upload the file.',
          });
        }
        console.warn('[UploadContext] poll error', e);
      }
    };
    tick(); // immediate first tick
    pollRefs.current[jobId] = setInterval(tick, POLL_MS);
  }, [_patchJob, _stopPoll]);

  /**
   * Start a background upload job.
   * Returns the job_id string, or throws on HTTP error (including 409 duplicate).
   */
  const startJob = useCallback(async (vendor, files, forceOverride = false) => {
    const fd = new FormData();
    fd.append('vendor', vendor);
    fd.append('force_override', String(forceOverride));
    files.forEach(f => fd.append('files', f));

    const { data } = await axios.post(`${API_BASE_URL}/api/upload`, fd);
    const jobId = data.job_id;

    setJobs(prev => [
      ...prev,
      {
        id:          jobId,
        vendor,
        filenames:   files.map(f => f.name),
        status:      'queued',
        progress:    0,
        total:       files.length,
        message:     'Queued for processing…',
        dismissed:   false,
        revision_updates: [],
        duplicate_info:   null,
      },
    ]);
    _startPoll(jobId);
    return jobId;
  }, [_startPoll]);

  /** Dismiss a finished/errored job from the widget. */
  const dismissJob = useCallback((jobId) => {
    _stopPoll(jobId);
    _patchJob(jobId, { dismissed: true });
  }, [_stopPoll, _patchJob]);

  /** Dismiss all jobs that are no longer active (done / error / duplicate). */
  const dismissAllSettled = useCallback(() => {
    setJobs(prev => {
      // Collect IDs of settled jobs that still have a running poller and stop
      // them now — all in one state updater so there are no side effects
      // scattered across multiple setJobs calls (which caused double-execution
      // issues in React StrictMode and race conditions in batched updates).
      prev.forEach(j => {
        if (!j.dismissed && !['queued', 'processing'].includes(j.status)) {
          if (pollRefs.current[j.id]) {
            clearInterval(pollRefs.current[j.id]);
            delete pollRefs.current[j.id];
          }
        }
      });
      return prev.map(j =>
        !j.dismissed && !['queued', 'processing'].includes(j.status)
          ? { ...j, dismissed: true }
          : j
      );
    });
  }, []);

  /**
   * Re-run a duplicate job with force_override=true.
   * Returns the new job_id, or throws (with a toast already queued) on failure.
   */
  const overrideJob = useCallback(async (jobId, filenamesToOverride = null) => {
    const body = filenamesToOverride ? { filenames: filenamesToOverride } : {};
    let data;
    try {
      ({ data } = await axios.post(`${API_BASE_URL}/api/jobs/${jobId}/override`, body));
    } catch (e) {
      // Determine a human-readable reason
      const is404 = e?.response?.status === 404;
      const errMsg = is404
        ? 'Override failed — the server was restarted and this job is no longer available. Please re-upload the file.'
        : `Override failed: ${e?.response?.data?.detail || e?.message || 'Unknown error'}`;

      // If the job is stale (404), dismiss the dead widget entry so it doesn't linger
      if (is404) {
        _stopPoll(jobId);
        _patchJob(jobId, { dismissed: true });
      }

      // Surface a toast so the user actually sees what went wrong
      setToasts(prev => [...prev, {
        id:       `override_err_${jobId}_${Date.now()}`,
        severity: 'error',
        msg:      errMsg,
      }]);
      throw e; // let the caller know it failed
    }

    const newJobId = data.job_id;
    // Mark the original job as dismissed so widget doesn't show both
    _patchJob(jobId, { dismissed: true });
    _stopPoll(jobId);
    // Add new job to list
    setJobs(prev => {
      const orig = prev.find(j => j.id === jobId) || {};
      return [
        ...prev,
        {
          id:               newJobId,
          vendor:           orig.vendor || '',
          filenames:        orig.filenames || [],
          status:           'queued',
          progress:         0,
          total:            (orig.filenames || []).length,
          message:          'Queued for processing (override)…',
          dismissed:        false,
          revision_updates: [],
          duplicate_info:   null,
        },
      ];
    });
    _startPoll(newJobId);
    return newJobId;
  }, [_patchJob, _stopPoll, _startPoll]);

  /**
   * Re-run a duplicate job with per-field merge decisions.
   * body: { filenames?: string[], field_decisions: { [field]: 'incoming'|'keep'|'fetch_br' } }
   * Returns the new job_id, or throws (with a toast already queued) on failure.
   */
  const fieldOverrideJob = useCallback(async (jobId, { filenames, field_decisions } = {}) => {
    const body = { field_decisions: field_decisions || {} };
    if (filenames && filenames.length > 0) body.filenames = filenames;
    let data;
    try {
      ({ data } = await axios.post(`${API_BASE_URL}/api/jobs/${jobId}/field-override`, body));
    } catch (e) {
      const status  = e?.response?.status;
      const detail  = e?.response?.data?.detail || '';
      // 404 can mean either the job is gone OR the endpoint doesn't exist yet (backend not deployed)
      const isJobGone      = status === 404 && (detail.toLowerCase().includes('job') || detail === '');
      const isEndpointGone = status === 404 && detail.toLowerCase().includes('not found') && !detail.toLowerCase().includes('job');
      const errMsg =
        isEndpointGone
          ? 'Override failed — the backend has not been updated yet. Please deploy the latest main_v2.py to EC2 and restart the service.'
          : isJobGone || status === 404
            ? 'Override failed — the server was restarted and this job is no longer available. Please re-upload the file.'
            : `Override failed: ${detail || e?.message || 'Unknown error'}`;
      if (status === 404) {
        _stopPoll(jobId);
        _patchJob(jobId, { dismissed: true });
      }
      setToasts(prev => [...prev, {
        id:       `field_override_err_${jobId}_${Date.now()}`,
        severity: 'error',
        msg:      errMsg,
      }]);
      throw e;
    }

    const newJobId = data.job_id;
    _patchJob(jobId, { dismissed: true });
    _stopPoll(jobId);
    setJobs(prev => {
      const orig = prev.find(j => j.id === jobId) || {};
      return [
        ...prev,
        {
          id:               newJobId,
          vendor:           orig.vendor || '',
          filenames:        orig.filenames || [],
          status:           'queued',
          progress:         0,
          total:            (orig.filenames || []).length,
          message:          'Queued for processing (field override)…',
          dismissed:        false,
          revision_updates: [],
          duplicate_info:   null,
        },
      ];
    });
    _startPoll(newJobId);
    return newJobId;
  }, [_patchJob, _stopPoll, _startPoll]);

  /**
   * Download the Excel result for a completed job.
   * Returns the revision_updates array (may be empty).
   */
  const downloadJob = useCallback(async (jobId) => {
    const r = await axios.get(`${API_BASE_URL}/api/jobs/${jobId}/download`, {
      responseType: 'blob',
    });
    const blob = r.data;
    const url  = window.URL.createObjectURL(blob);
    const a    = document.createElement('a');
    const disp = r.headers['content-disposition'] || '';
    const name = disp.match(/filename=(.+)/)?.[1] || 'result.xlsx';
    a.href     = url;
    a.download = name;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
    const revRaw = r.headers['x-revision-updates'];
    return revRaw ? JSON.parse(revRaw) : [];
  }, []);

  return (
    <UploadContext.Provider value={{
      jobs, startJob, dismissJob, dismissAllSettled, downloadJob, overrideJob, fieldOverrideJob,
      completionNotice, clearCompletionNotice,
      toasts, dismissToast,
    }}>
      {children}
    </UploadContext.Provider>
  );
}

export const useUpload = () => useContext(UploadContext);
